<div class="py-12 px-4 sm:px-6 lg:px-8">
  <div class="max-w-3xl mx-auto">
    <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6">Terms of Service</h1>
    <p class="text-gray-600 dark:text-gray-300 mb-4">Last Updated: May 22, 2025</p>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">1. About Us and Acceptance of Terms</h2>
      <p class="text-gray-600 dark:text-gray-300">
        These Terms of Service ("Terms") govern your use of services provided by {{ tradeName }}, ("we," "us," or "{{ tradeName }}").
      </p>
      <p class="text-gray-600 dark:text-gray-300 mt-2">
        By accessing or using our services, you agree to be bound by these Terms and our Privacy Policy. If you do not agree, you may not
        use our services.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">2. User Categories and Rights</h2>
      <p class="text-gray-600 dark:text-gray-300">
        <strong>Consumer Users:</strong> If you are an individual purchasing our services for personal use and are located in the European
        Union or UK, you have additional consumer protection rights as outlined in sections 4 and 9 of these Terms.
      </p>
      <p class="text-gray-600 dark:text-gray-300 mt-2">
        <strong>Business Users:</strong> If you are purchasing our services for business purposes, standard commercial terms apply
        throughout these Terms.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">3. Use of Services</h2>
      <p class="text-gray-600 dark:text-gray-300">
        You may use our services for lawful purposes only. You agree not to engage in illegal activities, hacking, spamming, fraud, or any
        misuse of the service. We reserve the right to suspend or terminate your account for violations of these Terms.
      </p>
      <p class="text-gray-600 dark:text-gray-300 mt-2">
        Our services include features for managing {{ organisationAlias }} entities, where Admins can view member lists. You are responsible
        for maintaining the confidentiality of your account credentials and for all activities under your account.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">4. Account and Authentication</h2>
      <p class="text-gray-600 dark:text-gray-300">
        To use our services, you must create an account using email sign-in, Google authentication, or enable 2-factor authentication. You
        are responsible for all activities under your account.
      </p>
      <p class="text-gray-600 dark:text-gray-300 mt-2">
        Account deletion results in immediate erasure of all associated data. We may retain your data for up to 12 months after inactivity
        for account recovery purposes, as detailed in our Privacy Policy. Data processing is based on contract performance (GDPR Article
        6(1)(b)) as necessary to provide our services.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">5. Payments and Consumer Withdrawal Rights</h2>
      <p class="text-gray-600 dark:text-gray-300">
        Paid plans are processed via Stripe and may auto-renew until canceled. All users receive a 30-day money-back guarantee on
        subscriptions.
      </p>
      <p class="text-gray-600 dark:text-gray-300 mt-2">
        <strong>EU/UK Consumer Rights:</strong> If you are a consumer in the European Union or UK, you have the right to withdraw from your
        purchase within 14 days of subscription without giving any reason. To exercise this right, contact us at {{ contactInfo.email }}. We
        will refund all payments received from you within 14 days of receiving your withdrawal notice.
      </p>
      <p class="text-gray-600 dark:text-gray-300 mt-2">
        By using our services immediately after purchase, you acknowledge that the withdrawal right may be waived for digital content that
        has been fully performed with your consent.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">6. Third-Party Services and Data Processing</h2>
      <p class="text-gray-600 dark:text-gray-300">
        Our services rely on third-party providers including Firebase (Google), Algolia, Stripe, and potentially analytics services like
        Google Analytics and Meta Pixel. Their terms and policies apply when you use features involving these services.
      </p>
      <p class="text-gray-600 dark:text-gray-300 mt-2">
        Data processing by these third parties may involve transfers outside the EU/EEA. We ensure GDPR compliance through Standard
        Contractual Clauses and other appropriate safeguards. See our Privacy Policy for detailed information about data processing and your
        rights.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">7. Termination</h2>
      <p class="text-gray-600 dark:text-gray-300">
        You may terminate your account at any time, and all data will be deleted immediately. We reserve the right to terminate or suspend
        your account for material violations of these Terms, providing reasonable notice where legally required.
      </p>
      <p class="text-gray-600 dark:text-gray-300 mt-2">
        Upon termination, your access to the services will cease, but these Terms will remain in effect for matters occurring before
        termination.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">8. Limitation of Liability</h2>
      <p class="text-gray-600 dark:text-gray-300">
        To the fullest extent permitted by law, {{ tradeName }} shall not be liable for any indirect, incidental, or consequential damages
        arising from your use of our services.
      </p>
      <p class="text-gray-600 dark:text-gray-300 mt-2">
        <strong>Consumer Protection:</strong> Nothing in these Terms excludes or limits our liability for death or personal injury caused by
        negligence, fraud, or any other liability that cannot be excluded under applicable consumer protection laws.
      </p>
      <p class="text-gray-600 dark:text-gray-300 mt-2">
        Our total liability to business users is limited to the amount paid by you for the services in the 12 months preceding the claim.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">9. Governing Law and Dispute Resolution</h2>
      <p class="text-gray-600 dark:text-gray-300">
        <strong>Governing Law:</strong> These Terms are governed by the laws of England and Wales without regard to conflict of law
        principles.
      </p>
      <p class="text-gray-600 dark:text-gray-300 mt-2">
        <strong>EU/UK Consumer Disputes:</strong> If you are a consumer in the EU or UK, you may bring disputes in the courts of your
        country of residence or our registered jurisdiction. You cannot be forced into binding arbitration and retain all statutory rights
        under consumer protection laws.
      </p>
      <p class="text-gray-600 dark:text-gray-300 mt-2">
        <strong>Business User Disputes:</strong> Business disputes will be resolved through binding arbitration unless both parties agree to
        court proceedings.
      </p>
      <p class="text-gray-600 dark:text-gray-300 mt-2">
        EU consumers may also use the European Commission's Online Dispute Resolution platform:
        <a class="text-blue-600 hover:text-blue-800" href="https://ec.europa.eu/consumers/odr/">https://ec.europa.eu/consumers/odr/</a>
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">10. Data Protection and Privacy</h2>
      <p class="text-gray-600 dark:text-gray-300">
        We process your personal data in accordance with our Privacy Policy and applicable data protection laws, including GDPR. You have
        the right to access, rectify, erase, restrict, object to processing, and port your data. You may also withdraw consent for analytics
        processing at any time.
      </p>
      <p class="text-gray-600 dark:text-gray-300 mt-2">
        To exercise your data protection rights, contact us at {{ contactInfo.email }}. You may also lodge complaints with your local data
        protection supervisory authority.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">11. Changes to Terms</h2>
      <p class="text-gray-600 dark:text-gray-300">
        We may update these Terms from time to time. We will notify you of material changes by email or through our service at least 30 days
        before they take effect. Your continued use after changes take effect constitutes acceptance of the new Terms.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-4">12. Contact Us</h2>
      <p class="text-gray-600 dark:text-gray-300">
        If you have any questions about these Terms, please contact us at {{ contactInfo.email }} or
        {{ contactInfo.phone.displayedNumber }}.
      </p>
    </section>
  </div>
</div>
